import { useState } from "react";
import { useQuery, useMutation } from "@tanstack/react-query";
import { toast } from "sonner";
import api, { errText } from "../lib/api";
import { Button } from "./ui/button";
import { ChalkboardTeacher, Sparkle, CircleNotch } from "@phosphor-icons/react";

export function CoachingDigest() {
  const [focus, setFocus] = useState(null);
  const { data } = useQuery({ queryKey: ["coaching-digest"], queryFn: () => api.get("/workflow/coaching-digest").then((r) => r.data) });
  const gen = useMutation({
    mutationFn: () => api.post("/workflow/coaching-digest/focus"),
    onSuccess: ({ data }) => { setFocus(data.focus); toast.success("Training focus generated"); },
    onError: (e) => toast.error(errText(e)),
  });
  if (!data) return null;
  const max = Math.max(...(data.top_mistakes || []).map((m) => m.count), 1);
  return (
    <div className="rounded-lg border border-zinc-200 bg-white p-4" data-testid="coaching-digest-card">
      <div className="flex items-center justify-between mb-1">
        <h3 className="text-sm font-medium text-zinc-900 flex items-center gap-1.5"><ChalkboardTeacher size={15} /> Coaching digest — repeated staff mistakes</h3>
        <Button data-testid="training-focus-btn" size="sm" variant="outline" className="h-7 text-[11px]" disabled={gen.isPending || !data.total_corrections} onClick={() => gen.mutate()}>
          {gen.isPending ? <CircleNotch size={12} className="animate-spin mr-1" /> : <Sparkle size={12} className="mr-1" />} Generate training focus
        </Button>
      </div>
      <p className="text-[10px] text-zinc-400 mb-3">{data.total_corrections} correction{data.total_corrections === 1 ? "" : "s"} logged · {data.corrections_this_week} this week · {data.lessons_in_index} lessons in the coaching index · {data.coach_acks?.verified || 0} verified / {data.coach_acks?.exception || 0} flagged by staff</p>
      {focus && (
        <div className="rounded-md bg-zinc-900 text-zinc-100 p-3 mb-3" data-testid="training-focus-text">
          <p className="text-[10px] uppercase tracking-wide text-zinc-400 mb-1">This week's training focus</p>
          <p className="text-[11px] whitespace-pre-wrap leading-relaxed">{focus}</p>
        </div>
      )}
      <div className="grid grid-cols-1 md:grid-cols-2 gap-4">
        <div>
          <p className="text-[10px] uppercase tracking-wide text-zinc-400 mb-1.5">Most repeated mistakes</p>
          <div className="space-y-2">
            {(data.top_mistakes || []).map((m) => (
              <div key={m.label} data-testid={`mistake-${m.label}`} title={m.example_comment || ""}>
                <div className="flex justify-between text-xs mb-0.5">
                  <span className="text-zinc-700 truncate pr-2">{m.label}{m.irc_refs?.length ? <span className="text-zinc-400"> · {m.irc_refs[0]}</span> : null}</span>
                  <span className="font-mono text-[10px] text-zinc-500 shrink-0">{m.count}× {m.this_week ? `(+${m.this_week} this wk)` : ""}</span>
                </div>
                <div className="h-1.5 bg-zinc-100 rounded-sm overflow-hidden"><div className="h-full bg-rose-500 rounded-sm" style={{ width: `${Math.round((m.count / max) * 100)}%` }} /></div>
              </div>
            ))}
            {!(data.top_mistakes || []).length && <p className="text-xs text-zinc-400 italic">No reviewer corrections yet.</p>}
          </div>
        </div>
        <div>
          <p className="text-[10px] uppercase tracking-wide text-zinc-400 mb-1.5">Coaching needs by preparer</p>
          <div className="space-y-1.5" data-testid="digest-by-staff">
            {(data.by_staff || []).map((s) => (
              <div key={s.staff} className="flex items-center justify-between text-xs border-b border-zinc-100 last:border-0 py-1.5">
                <span className="text-zinc-700">{s.staff}</span>
                <span className="text-[10px] text-zinc-500 text-right">{s.corrections} correction{s.corrections === 1 ? "" : "s"}{s.top_gap ? ` · gap: ${s.top_gap}` : ""}</span>
              </div>
            ))}
            {!(data.by_staff || []).length && <p className="text-xs text-zinc-400 italic">No data yet.</p>}
          </div>
        </div>
      </div>
    </div>
  );
}
